import { useEffect, useRef } from 'react'

// Findings #137: a capture saved offline keeps its "Sincronizando…" badge
// after the phone is back online — the screens read the outbox status once,
// when the first sync attempt ends, and the background flush that later
// sends the record never told them. While a badge is not final yet, re-read
// the status (read only — never pushes) every few seconds and when the
// phone comes back online or the screen regains focus.

export const RECHECK_UNSYNCED_INTERVAL_MS = 4000

export type CrewBadgeState = 'local' | 'pending' | 'synced' | 'error'

export const isUnsyncedBadgeState = (state: CrewBadgeState | null | undefined): boolean => (
  state === 'local' || state === 'pending'
)

export const useRecheckWhileUnsynced = (active: boolean, recheck: () => Promise<void> | void): void => {
  const recheckRef = useRef(recheck)
  recheckRef.current = recheck

  useEffect(() => {
    if (!active) return
    let running = false
    const run = () => {
      if (running) return
      running = true
      void Promise.resolve()
        .then(() => recheckRef.current())
        .catch(() => {})
        .finally(() => {
          running = false
        })
    }
    const timer = window.setInterval(run, RECHECK_UNSYNCED_INTERVAL_MS)
    window.addEventListener('online', run)
    window.addEventListener('focus', run)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('online', run)
      window.removeEventListener('focus', run)
    }
  }, [active])
}
